import React from 'react';
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Paperclip, MessageSquare, Clock } from "lucide-react";
import { cn } from "../ui/utils";
import { Ticket } from "./TicketCard";
import { Ticket as TicketIcon, ArrowUp, ArrowDown, CircleAlert } from "lucide-react";
import { Skeleton } from "../ui/skeleton";

interface ListViewProps {
  tickets: Ticket[];
  statusConfig: Record<string, { label: string; color: string; kanbanColor: string }>;
  onTicketClick?: (ticket: Ticket) => void;
  emptyState?: React.ReactNode;
  isLoading?: boolean;
}

const PRIORITY_STYLES: Record<string, { label: string; color: string; icon: any }> = {
  Urgent: { label: "Urgent", color: "bg-red-100 text-red-700", icon: ArrowUp },
  High: { label: "High", color: "bg-orange-100 text-orange-600", icon: ArrowUp },
  Medium: { label: "Medium", color: "bg-blue-100 text-blue-700", icon: ArrowDown },
  Low: { label: "Low", color: "bg-gray-100 text-gray-600", icon: ArrowDown },
};

export const TicketListView: React.FC<ListViewProps> = ({ 
  tickets, 
  statusConfig, 
  onTicketClick,
  emptyState,
  isLoading
}) => {
  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-gray-50 border-b">
            <TableRow className="hover:bg-transparent border-none">
              <TableHead className="w-24 font-bold text-gray-700">ID</TableHead>
              <TableHead className="min-w-[200px] font-bold text-gray-700">Title</TableHead>
              <TableHead className="font-bold text-gray-700">Service Desk</TableHead>
              <TableHead className="font-bold text-gray-700">Status</TableHead>
              <TableHead className="font-bold text-gray-700">Priority</TableHead>
              <TableHead className="font-bold text-gray-700">Submitter</TableHead>
              <TableHead className="font-bold text-gray-700">Assigned To</TableHead>
              <TableHead className="font-bold text-gray-700">Date / SLA</TableHead>
              <TableHead className="text-right font-bold text-gray-700">Info</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} className="hover:bg-transparent">
                  <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-20 rounded-sm" /></TableCell>
                  <TableCell><Skeleton className="h-5 w-16 rounded-md" /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-7 w-7 rounded-full" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </TableCell>
                  <TableCell><Skeleton className="h-7 w-7 rounded-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Skeleton className="h-4 w-6" />
                      <Skeleton className="h-4 w-6" />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : tickets.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={9} className="p-0 border-none">
                  {emptyState || (
                    <div className="flex flex-col items-center justify-center p-20 text-center">
                      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <TicketIcon className="h-6 w-6 text-gray-500" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900">No tickets found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        There are currently no service requests to display.
                      </p>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket) => {
                const Priority = PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.Medium;
                const PriorityIcon = Priority.icon;

                return (
                  <TableRow 
                    key={ticket.id} 
                    className="cursor-pointer hover:bg-gray-50/50 transition-colors group"
                    onClick={() => onTicketClick?.(ticket)}
                  >
                    <TableCell className="font-medium text-blue-600">{ticket.formattedId}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm group-hover:text-blue-600 transition-colors">{ticket.title}</span>
                        <span className="text-[11px] text-gray-500">{ticket.category}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{ticket.serviceDesk}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("text-[10px] uppercase font-bold rounded-sm border", statusConfig[ticket.status]?.color || "bg-gray-100 text-gray-500")}>
                        {statusConfig[ticket.status]?.label || ticket.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className={cn("flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase w-fit", Priority.color)}>
                         <PriorityIcon className="w-3 h-3" />
                         {Priority.label}
                      </div>
                    </TableCell>
                    <TableCell>
                       <div className="flex items-center gap-2">
                          <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white", ticket.submitter.color)}>
                            {ticket.submitter.initials}
                          </div>
                          <span className="text-xs font-medium">{ticket.submitter.name}</span>
                       </div>
                    </TableCell>
                    <TableCell>
                       {ticket.assignees && ticket.assignees.length > 0 ? (
                         <div className="flex -space-x-2">
                            {ticket.assignees.slice(0, 3).map((assignee, idx) => (
                                <div 
                                    key={idx}
                                    className={cn("w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-white", assignee.color)}
                                    title={assignee.name}
                                >
                                    {assignee.initials}
                                </div>
                            ))}
                            {ticket.assignees.length > 3 && (
                                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[8px] font-bold text-gray-500 border-2 border-white">
                                    +{ticket.assignees.length - 3}
                                </div>
                            )}
                         </div>
                       ) : (
                         <span className="text-xs text-gray-400 italic font-medium">Unassigned</span>
                       )}
                    </TableCell>
                    <TableCell>
                      {ticket.isBreached ? (
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-red-600 bg-red-50 w-fit px-2 py-0.5 rounded border border-red-100">
                          <CircleAlert size={12} />
                          BREACHED
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[10px] font-medium text-gray-500">
                          <Clock size={12} />
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2 text-gray-400">
                         {ticket.attachments > 0 && <div className="flex items-center gap-0.5 text-xs"><Paperclip size={14} /> {ticket.attachments}</div>}
                         {ticket.comments > 0 && <div className="flex items-center gap-0.5 text-xs"><MessageSquare size={14} /> {ticket.comments}</div>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
